/**
 * TemplatesCard — общие шаблоны WhatsApp платформы: одни на все клиники.
 *
 * Сам текст шаблона живёт в кабинете Meta и меняется только через её
 * проверку — здесь мы правим то, чего Meta не знает: человеческое
 * название и подписи переменных. Клиника в своём кабинете видит не
 * «Здравствуйте, {{1}}», а «Здравствуйте, [Имя]» — и эти подписи
 * задаются отсюда. Поэтому в предпросмотре подставляем их сразу:
 * проверять подписи по голым `{{1}}` бессмысленно.
 *
 * Статус показываем у каждого: отправка идёт только по одобренным, а
 * Meta умеет отклонить или приостановить шаблон молча, без нашего
 * участия. Причину она объясняет текстом — его и показываем.
 */

import {useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {Pencil} from 'lucide-react';

import {type Template, templatesQueryOptions} from '@services/templates';
import {Loader} from '@components/ui/Loader';
import {cn} from '@lib/utils';

import {TemplateLabelsModal} from '../TemplateLabelsModal';

import {previewWithLabels, statusClass, statusLabel} from './TemplatesCard.utils';

export const TemplatesCard = () => {
  const [editing, setEditing] = useState<Template | null>(null);
  const {data, isLoading, isError} = useQuery(templatesQueryOptions());

  return (
    <div className="rounded-[13px] border border-skr-line bg-card">
      <div className="px-4 py-3">
        <div className="text-sm font-semibold">Общие шаблоны WhatsApp</div>
        <div className="mt-0.5 text-[12px] leading-[1.45] text-muted-foreground">
          Доступны всем клиникам. Текст меняется только в кабинете Meta — здесь название и подписи
          переменных, которые видит клиника.
        </div>
      </div>

      {isLoading ? (
        <div className="flex min-h-[160px] items-center justify-center border-t border-skr-hairline">
          <Loader label="Загружаем шаблоны…" />
        </div>
      ) : isError ? (
        <div className="border-t border-skr-hairline px-4 py-8 text-center text-[13px] text-destructive">
          Не удалось загрузить шаблоны
        </div>
      ) : (data?.length ?? 0) === 0 ? (
        <div className="border-t border-skr-hairline px-4 py-8 text-center text-[13px] text-muted-foreground">
          Общих шаблонов пока нет — заведите их в кабинете Meta
        </div>
      ) : (
        <ul>
          {data?.map((t) => (
            <li key={t.id} className="border-t border-skr-hairline px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[13.5px] font-semibold">{t.title || t.metaName}</span>
                    <span
                      className={cn(
                        'rounded-full px-2 py-0.5 text-[11px] font-semibold',
                        statusClass(t.status),
                      )}
                    >
                      {statusLabel(t.status)}
                    </span>
                  </div>
                  <div className="mt-0.5 font-mono text-[11.5px] text-skr-faint">
                    {t.metaName} · {t.lang} · {t.category}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setEditing(t)}
                  aria-label="Изменить название и подписи"
                  className="grid size-8 shrink-0 place-items-center rounded-[9px] bg-secondary text-muted-foreground transition hover:text-foreground"
                >
                  <Pencil className="size-[15px]" strokeWidth={2} />
                </button>
              </div>

              <div className="mt-2 whitespace-pre-line rounded-[10px] bg-skr-head px-[13px] py-2 text-[12.5px] leading-[1.5] text-skr-ink2">
                {previewWithLabels(t.bodyPreview, t.variableLabels)}
              </div>

              {t.unavailableReason !== null && (
                <div className="mt-1.5 text-[11.5px] leading-[1.45] text-destructive">
                  {t.unavailableReason}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Пересоздаём модалку на каждый шаблон: внутри живут поля формы
          и состояние мутации, и без этого ошибка сохранения одного
          шаблона встречала бы человека в форме следующего. */}
      <TemplateLabelsModal
        key={editing?.id ?? 'none'}
        template={editing}
        onClose={() => setEditing(null)}
      />
    </div>
  );
};
